// src/pages/clientes/components/ContactosTab.tsx
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Card,
  CardContent,
  Button,
  Badge,
  Input,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Checkbox,
} from '@/components/ui';
import { EmptyState } from '@/components/common/EmptyState';
import { Skeleton } from '@/components/common/Skeleton';
import { Plus, User, Trash2, Mail, Phone, Star } from 'lucide-react';
import { useContactos, useCrearContacto, useEliminarContacto } from '@/hooks/queries/useClientes';
import { contactoSchema, type ContactoFormData } from '../schemas/clienteSchema';
import type { Contacto } from '@/types';

interface Props {
  clienteId: string;
}

export function ContactosTab({ clienteId }: Props) {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [contactoToDelete, setContactoToDelete] = useState<Contacto | null>(null);

  const { data: contactos, isLoading } = useContactos(clienteId);
  const crearMutation = useCrearContacto(clienteId);
  const eliminarMutation = useEliminarContacto(clienteId);

  const form = useForm<ContactoFormData>({
    resolver: zodResolver(contactoSchema),
    defaultValues: {
      nombre: '',
      email: '',
      telefono: '',
      cargo: '',
      es_principal: false,
    },
  });

  const onSubmit = async (data: ContactoFormData) => {
    try {
      await crearMutation.mutateAsync(data);
      form.reset();
      setIsFormOpen(false);
    } catch {
      // Error handled by mutation
    }
  };

  const handleDelete = async () => {
    if (!contactoToDelete) return;
    try {
      await eliminarMutation.mutateAsync(contactoToDelete.id);
      setContactoToDelete(null);
    } catch {
      // Error handled by mutation
    }
  };

  if (isLoading) {
    return (
      <Card className="mt-4">
        <CardContent className="pt-6">
          <div className="space-y-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="flex items-center gap-4">
                <Skeleton className="h-10 w-10 rounded-full" />
                <div className="space-y-2">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-3 w-48" />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="mt-4">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="font-semibold">Contactos del cliente</h3>
          <Button size="sm" onClick={() => setIsFormOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Nuevo Contacto
          </Button>
        </div>
        <CardContent className="pt-4">
          {contactos && contactos.length > 0 ? (
            <div className="space-y-3">
              {contactos.map((contacto: Contacto) => (
                <div
                  key={contacto.id}
                  className="flex items-center justify-between p-3 rounded-lg border hover:bg-gray-50"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100">
                      <User className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium">{contacto.nombre}</p>
                        {contacto.es_principal && (
                          <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-sm text-gray-500">
                        {contacto.cargo && <span>{contacto.cargo}</span>}
                        {contacto.email && (
                          <span className="flex items-center gap-1">
                            <Mail className="h-3 w-3" />
                            {contacto.email}
                          </span>
                        )}
                        {contacto.telefono && (
                          <span className="flex items-center gap-1">
                            <Phone className="h-3 w-3" />
                            {contacto.telefono}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={contacto.activo ? 'success' : 'default'}>
                      {contacto.activo ? 'Activo' : 'Inactivo'}
                    </Badge>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setContactoToDelete(contacto)}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <EmptyState
              icon={<User className="h-12 w-12" />}
              title="Sin contactos"
              description="Este cliente no tiene contactos registrados"
              action={{
                label: 'Agregar contacto',
                onClick: () => setIsFormOpen(true),
              }}
            />
          )}
        </CardContent>
      </Card>

      {/* Dialog de nuevo contacto */}
      <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nuevo Contacto</DialogTitle>
          </DialogHeader>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <Input
              label="Nombre *"
              placeholder="Nombre del contacto"
              {...form.register('nombre')}
              error={form.formState.errors.nombre?.message}
            />
            <Input
              label="Cargo"
              placeholder="Ej: Gerente de TI"
              {...form.register('cargo')}
              error={form.formState.errors.cargo?.message}
            />
            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Email"
                type="email"
                placeholder="contacto@empresa.com"
                {...form.register('email')}
                error={form.formState.errors.email?.message}
              />
              <Input
                label="Teléfono"
                placeholder="+51 999 999 999"
                {...form.register('telefono')}
                error={form.formState.errors.telefono?.message}
              />
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="es_principal"
                checked={form.watch('es_principal')}
                onCheckedChange={(checked) =>
                  form.setValue('es_principal', checked === true)
                }
              />
              <label htmlFor="es_principal" className="text-sm cursor-pointer">
                Contacto principal
              </label>
            </div>
            <DialogFooter>
              <Button type="button" variant="secondary" onClick={() => setIsFormOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" loading={crearMutation.isPending}>
                Crear
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog de confirmación de eliminación */}
      <Dialog open={!!contactoToDelete} onOpenChange={() => setContactoToDelete(null)}>
        <DialogContent size="sm">
          <DialogHeader>
            <DialogTitle>Eliminar Contacto</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600">
            ¿Estás seguro de que deseas eliminar el contacto{' '}
            <span className="font-semibold">{contactoToDelete?.nombre}</span>?
          </p>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setContactoToDelete(null)}>
              Cancelar
            </Button>
            <Button variant="danger" onClick={handleDelete} loading={eliminarMutation.isPending}>
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
